/* eslint-disable react/jsx-no-bind */

import { faCopy } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment-timezone';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { alphabeticalSortingComparator, ShopViewModel, timezones, timezoneAliases } from 'stageclip-common';
import { FormSection } from 'redux-form';

import { createModalOpenAction } from '../../actions/modalActions';
import { createRequestAllShopsAction } from '../../actions/shopActions';
import { ApiDataTypes } from '../../api/apiDataTypes';
import { FormId } from '../../constants/formId';
import { ClippingPlatformState } from '../../store/clippingPlatformState';
import { getAll } from '../../store/selectors';
import Button from '../buttons/button';
import ClipConfigurationForm from '../clipConfiguration/clipConfigurationForm';
import {
  baseMapDispatchToProps,
  baseMapStateToProps,
  ComponentBase,
  BaseDispatchProps,
  BaseStateProps,
} from '../componentBase';
import { AppSelectField } from '../form/appSelectField';
import { AppTextField } from '../form/appTextField';
import { AppFileField } from '../form/appFileField';
import { AppNumberField } from '../form/appNumberField';
import { ToggleFieldComponent } from '../form/toggle/toggleField.component';
import { ColorPickerFieldComponent } from '../form/color-picker/color-picker-field.component';
import { required, minValue } from '../form/formValidations';
import { formatTimezoneLabel } from '../../helpers/dateHelpers';

import styles from './baseClientForm.module.scss';

const months = moment.months().map((month, index) => ({ label: month, value: index + 1 }));

const studentsAmountValidators = [minValue(0)];

interface BaseClientFormOwnProps {
  formId: FormId;
  onTimezoneChange: (isDst: boolean) => void;
}

interface BaseClientFormStateProps extends BaseStateProps {
  shops: ShopViewModel[];
  shopsLoaded: boolean;
  formData: any;
}

interface BaseClientFormDispatchProps extends BaseDispatchProps {
  loadShops: (c: BaseClientForm) => void;
  openCopyClipTemplateConfigForm: (formId: string) => void;
}

type BaseClientFormState = { dstDisabled: boolean };

class BaseClientForm extends ComponentBase<
  BaseClientFormOwnProps,
  BaseClientFormStateProps,
  BaseClientFormDispatchProps,
  BaseClientFormState
> {
  constructor(props: any) {
    super(props);

    this.state = {
      dstDisabled: false,
    };
  }

  protected async loadData(props: any) {
    if (!props.shopsLoaded) {
      await props.loadShops(this);
    }
  }

  protected dataLoaded(props: any): boolean {
    if (props.formData?.timezone && timezones[props.formData.timezone] === undefined) {
      // eslint-disable-next-line no-param-reassign
      props.formData.timezone = timezoneAliases[props.formData.timezone];
    }

    return props.shopsLoaded;
  }

  protected loggedInRender() {
    const timezoneOptions = moment.tz
      .names()
      .filter(el => timezones[el] !== undefined)
      .reduce((memo, tz) => {
        memo.push({
          name: tz,
          offset: moment.tz(tz).utcOffset(),
        });

        return memo;
      }, [])
      .sort((a, b) => a.offset - b.offset)
      .map(timezone => ({
        value: timezone.name,
        label: formatTimezoneLabel(timezone.name),
      }));
    const boundOpenCopyClipTemplateConfigForm = this.openCopyClipTemplateConfig.bind(this);

    const handleTimezoneChange = (e: any) => {
      const selectedTimezone = Object.values(e)
        .filter(el => typeof el === 'string')
        .join('');

      const janOffset = moment
        .tz(selectedTimezone)
        .set({ month: 0, day: 1 })
        .utcOffset();
      const julOffset = moment
        .tz(selectedTimezone)
        .set({ month: 6, day: 1 })
        .utcOffset();

      const isDst = janOffset - julOffset !== 0;

      this.setState({ dstDisabled: !isDst });
      this.props.onTimezoneChange(isDst);
    };

    return (
      <>
        <AppTextField name="name" label="Client name" validate={[required]} />
        <AppTextField name="domain" label="Domain name" />
        <AppSelectField
          name="timezone"
          label="Client’s timezone"
          validate={[required]}
          options={timezoneOptions}
          onChange={handleTimezoneChange}
        />
        <ToggleFieldComponent
          name="dst"
          label="Adjust for daylight saving time automatically"
          disabled={this.state.dstDisabled}
        />
        <AppSelectField name="eventMonths" label="Event month" options={months} multi />
        <AppTextField name="googleAnalyticsId" label="Google Analytics View ID" />
        <AppTextField name="youTubePlaylistId" label="YouTube playlist ID" />
        <ToggleFieldComponent name="survey" label="Survey" />
        <ToggleFieldComponent name="emails" label="E-mails" />
        <ToggleFieldComponent name="clipTemplateEditor" label="Clip Template Editor" />
        <AppNumberField
          name="studentsAmount"
          label="Number of graduates per year"
          validate={studentsAmountValidators}
          integer
          positive
        />
        <AppSelectField name="shopId" label="Shop" options={this.getShopsOptions()} />
        <FormSection name="colors">
          <div>Colours</div>
          <ColorPickerFieldComponent name="primary" label="Primary colour" />
          <ColorPickerFieldComponent name="secondary" label="Secondary colour" />
        </FormSection>

        <AppFileField name="logo" label="Logo" extensions={['png', 'jpg', 'gif', 'svg']} />
        <Button
          text="Copy client configuration"
          icon={faCopy}
          onClick={boundOpenCopyClipTemplateConfigForm}
          allCapitals
          type="button"
          className={styles.copyClientConfigurationButton}
        />
        <ClipConfigurationForm formId={this.props.formId} />
      </>
    );
  }

  private openCopyClipTemplateConfig() {
    this.props.openCopyClipTemplateConfigForm(this.props.formId);
  }

  private getShopsOptions() {
    return this.props.shops
      .sort((a: any, b: any) => alphabeticalSortingComparator(a.name, b.name))
      .map((shop: ShopViewModel) => ({ value: shop.id, label: shop.name }));
  }
}

function mapStateToProps(state: ClippingPlatformState): BaseClientFormStateProps {
  const form = state.form[FormId.EditClientForm];

  return {
    ...baseMapStateToProps(state),
    shops: getAll(state.entities.shops),
    shopsLoaded: state.appStatus.allLoaded[ApiDataTypes.Shops],
    formData: form ? form.values : undefined,
  };
}

function mapDispatchToProps(dispatch: Dispatch): BaseClientFormDispatchProps {
  return {
    ...baseMapDispatchToProps(dispatch),
    loadShops: (c: BaseClientForm) => dispatch(createRequestAllShopsAction(c, dispatch)),
    openCopyClipTemplateConfigForm: (formId: string) =>
      dispatch(createModalOpenAction(FormId.CopyClipConfigurationFromClient, { formId })),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(BaseClientForm);
